import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Loader2,
  Upload,
  Download,
  Link as LinkIcon,
  FileText,
  CheckCircle2,
  AlertTriangle,
  FileUp,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const ExamImporter = ({ open, onOpenChange, token, onImport, title }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  const [tab, setTab] = useState('docx');
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [sourceLabel, setSourceLabel] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const resetState = () => {
    setParsed([]);
    setWarnings([]);
    setSourceLabel('');
    setFormUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (parsed.length === 0) { toast.error('لا توجد أسئلة للاستيراد'); return; }
    onImport(parsed);
    handleClose(false);
  };

  // ── Word upload ────────────────────────────────────────────────────────────

  const handleDownloadTemplate = async () => {
    try {
      const r = await fetch(`${API_URL}/api/questions/template/docx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error('تعذّر تنزيل القالب'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'قالب_استيراد_الاسئلة.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('حدث خطأ أثناء تنزيل القالب');
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('يجب رفع ملف بصيغة .docx فقط');
      return;
    }

    setLoading(true);
    setParsed([]);
    setWarnings([]);

    try {
      const fd = new FormData();
      fd.append(
        'file',
        file.slice(0, file.size, file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'),
        'upload.docx',
      );
      const r = await fetch(`${API_URL}/api/questions/parse/docx`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Original-Filename': encodeURIComponent(file.name),
        },
        body: fd,
      });
      const raw = await r.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { /* not JSON */ }

      if (!r.ok || !data) {
        toast.error(`فشل الاستيراد: ${data?.detail || raw?.slice(0, 200) || 'لا يوجد رد من الخادم'}`);
      } else {
        setParsed(data.questions || []);
        setWarnings(data.warnings || []);
        setSourceLabel(file.name);
        if ((data.questions || []).length > 0) {
          toast.success(`تم استخراج ${data.count} سؤال بنجاح ✓`);
        } else {
          toast.warning('لم يتم العثور على أسئلة في الملف');
        }
      }
    } catch (err) {
      toast.error(`خطأ في الاتصال: ${err.message || err}`);
    }

    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => uploadFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  // ── Google Forms ───────────────────────────────────────────────────────────

  const handleGoogleFormFetch = async () => {
    if (!formUrl.trim()) { toast.error('أدخل رابط النموذج'); return; }
    setLoading(true);
    setParsed([]);
    setWarnings([]);
    try {
      const r = await fetch(`${API_URL}/api/questions/parse/google-form`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: formUrl.trim() }),
      });
      const raw = await r.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { /* not JSON */ }
      if (!r.ok || !data) {
        toast.error(`فشل (${r.status}): ${data?.detail || raw?.slice(0, 200) || 'لا يوجد رد'}`);
      } else {
        setParsed(data.questions || []);
        setWarnings(data.warnings || []);
        setSourceLabel(formUrl.trim());
        toast.success(`تم استخراج ${data.count} سؤال`);
      }
    } catch (err) {
      toast.error(`خطأ في الاتصال: ${err.message || err}`);
    }
    setLoading(false);
  };

  // ── render ─────────────────────────────────────────────────────────────────

  const hasParsed = parsed.length > 0;
  const hasWarnings = warnings.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col">

        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b bg-white shrink-0">
          <DialogTitle className="text-lg font-bold text-gray-800" dir="rtl">
            {title || 'استيراد الأسئلة'}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-0.5" dir="rtl">
            استيراد أسئلة الاختيار من متعدد من ملف Word أو Google Forms
          </p>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          <Tabs value={tab} onValueChange={(v) => { setTab(v); resetState(); }}>
            <TabsList className="grid w-full grid-cols-2 bg-gray-100 rounded-xl h-11">
              <TabsTrigger value="docx" className="rounded-lg flex items-center gap-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <FileText className="w-4 h-4" />
                ملف Word
              </TabsTrigger>
              <TabsTrigger value="gform" className="rounded-lg flex items-center gap-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <LinkIcon className="w-4 h-4" />
                Google Forms
              </TabsTrigger>
            </TabsList>

            {/* ────────── Word tab ────────── */}
            <TabsContent value="docx" className="mt-5 space-y-4" dir="rtl">

              {/* Steps card */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                <p className="font-semibold text-gray-700 text-sm">طريقة تحضير الملف</p>

                <div className="space-y-3">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                      ١
                    </span>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">نص السؤال</span> في سطر مستقل
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                      ٢
                    </span>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">الخيارات في السطر التالي</span> — كل خيار يبدأ بـ{' '}
                      {['أ-', 'ب-', 'ج-', 'د-'].map((l) => (
                        <span key={l} className="inline-block mx-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-mono text-xs font-bold">
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-gray-300 text-gray-600 text-xs font-bold flex items-center justify-center mt-0.5">
                      ٣
                    </span>
                    <div className="text-sm text-gray-500">
                      اختياري: إذا كانت الخيارات طويلة يمكن تقسيمها على سطرين{' '}
                      <span className="font-medium text-gray-600">(أ- ب-)</span> ثم{' '}
                      <span className="font-medium text-gray-600">(ج- د-)</span>
                    </div>
                  </div>
                </div>

                {/* Example box */}
                <div className="rounded-lg border border-amber-200 bg-white overflow-hidden">
                  <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 font-medium">
                    مثال
                  </div>
                  <div className="px-3 py-2.5 font-mono text-xs leading-6 text-gray-700 space-y-0.5" dir="rtl">
                    <div>صورة المبتدأ في جملة: (هذا اللاعب سريع):</div>
                    <div className="text-amber-800">أ- اسم ظاهر.&emsp;&emsp;ب- اسم إشارة.&emsp;&emsp;ج- اسم موصول.&emsp;&emsp;د- اسم استفهام.</div>
                  </div>
                </div>

                {/* Download template */}
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 text-sm text-amber-700 hover:text-amber-900 font-medium transition-colors"
                >
                  <Download className="w-4 h-4" />
                  تنزيل قالب جاهز (.docx)
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Drop zone */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                className="hidden"
                onChange={handleFileChange}
              />

              <div
                onClick={() => !loading && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={[
                  'relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition-all select-none',
                  loading
                    ? 'border-amber-300 bg-amber-50 cursor-default'
                    : dragOver
                      ? 'border-amber-500 bg-amber-50 scale-[1.01]'
                      : 'border-gray-300 bg-gray-50 hover:border-amber-400 hover:bg-amber-50',
                ].join(' ')}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                    <p className="text-sm font-medium text-amber-700">جارٍ قراءة الملف...</p>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                      <FileUp className="w-7 h-7 text-amber-600" />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-sm font-semibold text-gray-700">
                        {dragOver ? 'أفلت الملف هنا' : 'اسحب ملف Word هنا أو اضغط للاختيار'}
                      </p>
                      <p className="text-xs text-gray-400">صيغة .docx فقط — بحجم أقصاه 10 ميجابايت</p>
                    </div>
                    <span className="px-4 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors">
                      <Upload className="w-4 h-4 inline-block ml-1.5 -mt-0.5" />
                      اختر ملف
                    </span>
                  </>
                )}
              </div>
            </TabsContent>

            {/* ────────── Google Forms tab ────────── */}
            <TabsContent value="gform" className="mt-5 space-y-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm text-gray-700" dir="rtl">
                <p className="font-semibold">طريقة الاستخدام:</p>
                <ol className="list-decimal pr-5 space-y-1.5">
                  <li>افتح النموذج في Google Forms ثم اضغط <b>Send / إرسال</b>.</li>
                  <li>اختر أيقونة الرابط 🔗 وانسخ الرابط.</li>
                  <li>الصق الرابط في الحقل أدناه.</li>
                </ol>
                <p className="text-xs text-gray-500">
                  يدعم استيراد أسئلة الاختيار من متعدد فقط. الإجابة الصحيحة تُضبط يدوياً بعد الاستيراد.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gform-url" className="text-sm font-medium text-gray-700">رابط النموذج</Label>
                <Input
                  id="gform-url"
                  dir="ltr"
                  placeholder="https://docs.google.com/forms/d/e/.../viewform"
                  value={formUrl}
                  onChange={(e) => setFormUrl(e.target.value)}
                  className="text-sm"
                />
              </div>
              <Button
                onClick={handleGoogleFormFetch}
                disabled={loading || !formUrl.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700 gap-2"
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> جارٍ القراءة...</>
                  : <><LinkIcon className="w-4 h-4" /> قراءة النموذج</>
                }
              </Button>
            </TabsContent>
          </Tabs>

          {/* ── Results ── */}
          {(hasParsed || hasWarnings) && (
            <div className="space-y-3" dir="rtl">

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">نتيجة الاستيراد</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Warnings */}
              {hasWarnings && (
                <div className="flex gap-2.5 rounded-xl bg-yellow-50 border border-yellow-200 p-3">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-yellow-500" />
                  <ul className="space-y-0.5 text-xs text-yellow-800">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Success summary */}
              {hasParsed && (
                <div className="flex items-center justify-between rounded-xl bg-green-50 border border-green-200 px-4 py-2.5">
                  <span className="flex items-center gap-2 text-sm font-semibold text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    تم استخراج {parsed.length} سؤال
                  </span>
                  {sourceLabel && (
                    <span className="text-xs text-green-600 max-w-[45%] truncate" title={sourceLabel}>
                      {sourceLabel}
                    </span>
                  )}
                </div>
              )}

              {/* Question cards */}
              {hasParsed && (
                <div className="max-h-56 overflow-y-auto space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  {parsed.map((q, i) => (
                    <div key={i} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm">
                      <p className="font-medium text-gray-800 leading-relaxed">
                        <span className="text-gray-400 text-xs font-normal ml-1">{i + 1}.</span>
                        {q.text}
                      </p>
                      {q.options?.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-1">
                          {q.options.map((o, oi) => {
                            const letters = ['أ', 'ب', 'ج', 'د', 'هـ', 'و'];
                            const isCorrect = o.id === q.correct_answer;
                            return (
                              <div
                                key={o.id}
                                className={[
                                  'flex items-start gap-1.5 rounded-md px-2 py-1 text-xs',
                                  isCorrect
                                    ? 'bg-green-50 text-green-800 font-semibold'
                                    : 'text-gray-600',
                                ].join(' ')}
                              >
                                <span className={[
                                  'shrink-0 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center mt-px',
                                  isCorrect ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500',
                                ].join(' ')}>
                                  {letters[oi] || oi + 1}
                                </span>
                                {o.text}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-white shrink-0 flex gap-2">
          <Button variant="outline" onClick={() => handleClose(false)} className="flex-1 sm:flex-none">
            إلغاء
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!hasParsed}
            className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700 font-semibold gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            {hasParsed ? `إضافة ${parsed.length} سؤال` : 'إضافة الأسئلة'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExamImporter;
